import { Inject, Injectable } from '@angular/core'
import {
  API_CONFIGURATION,
  AuthEvent,
  AuthEventType,
  FullApiClientConfiguration,
  SocketIoService,
} from '@ftr/api-shared'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { decodeObjectStrings } from '@ftr/contracts/shared/sanitizer'
import { JoinRoomRequest, getRoomId as getJoinRef } from '@ftr/contracts/socket/join-room/JoinRoomRequest'
import { JoinRoomResponse } from '@ftr/contracts/socket/join-room/JoinRoomResponse'
import { Message } from '@ftr/contracts/type/shared'
import { ClassConstructor, classToPlain, plainToClass } from '@ftr/serialization'
import { LoggingService } from '@ftr/ui-observability'
import { Observable, Observer } from 'rxjs'
import { Socket } from 'socket.io-client'
import { AuthenticationService } from '../authentication.service'

/**
 * A map of message name to the observable which emits messages for that message
 */
type MessageObservableMap = Map<string, Observable<object>>

interface JoinInfo {
  joinRoomRequest: JoinRoomRequest
  messageObservableMap: MessageObservableMap
  onErrorJoiningRoom?: () => void
  roomIds?: string[]
}
@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket: Socket
  /**
   * A map of join requests into their <code>MessageObservableMap</code> which are currently in use
   *
   * @see MessageObservableMap for more details
   */
  private readonly joins: Map<string, JoinInfo>

  constructor(
    @Inject(API_CONFIGURATION) readonly configuration: FullApiClientConfiguration,
    private readonly loggingService: LoggingService,
    private readonly authenticationService: AuthenticationService,
    private readonly socketIoService: SocketIoService,
  ) {
    this.joins = new Map<string, JoinInfo>()
    this.socket = this.socketIoService.connect(configuration.socket.url)
    this.authenticationService.authEvents.subscribe(this.onAuthEvent.bind(this))
    this.logEventsAndErrors()
    this.setupReconnect()
  }

  /**
   * Returns an observable of socket messages for the provided message type in the provided room. If the room has not
   * yet been joined, joins the socket room.
   *
   * When all observables within a socket room have been torn down (i.e.: unsubscribed from), leaves the room.
   *
   * IMPORTANT: Events emitted by the returned observable are only filtered by message type, not by the room which emits
   * the message. If the message type can be emitted by multiple different rooms (e.g.: recording specific events), it
   * is the consumer's responsibility to filter out this message. This is because socket.io does not provide data on
   * which room emitted a certain message.
   */
  onMessage<T extends Message>(
    joinRoomRequest: JoinRoomRequest,
    constructor: ClassConstructor<T>,
    onErrorJoiningRoom?: () => void,
  ): Observable<T> {
    const messageName = new constructor().$name

    const joinRef = getJoinRef(joinRoomRequest)
    let joinInfo = this.joins.get(joinRef)
    if (!joinInfo) {
      joinInfo = {
        joinRoomRequest,
        messageObservableMap: new Map<string, Observable<object>>(),
        onErrorJoiningRoom,
      }
      this.joins.set(joinRef, joinInfo)
      this.joinRoomAndHandleErrors(joinInfo)
    }

    if (!joinInfo!.messageObservableMap.has(messageName)) {
      const observable = new Observable<T>((observer: Observer<T>) => {
        this.socket.on(messageName, (data: any) => {
          this.loggingService.debug({
            message: 'Received socket event',
            type: messageName,
            data,
          })
          // Decode incoming data before converting to class to match what is done for all http requests
          const decodedData = decodeObjectStrings(data)
          observer.next(plainToClass(constructor, decodedData as object))
        })

        return () => {
          const latestMap = this.joins.get(joinRef)?.messageObservableMap
          if (!latestMap) {
            return
          }
          latestMap.delete(messageName)
          if (latestMap.size === 0) {
            this.leaveRooms(joinRef)
            this.joins.delete(joinRef)
          }
        }
      })
      joinInfo.messageObservableMap.set(messageName, observable)
    }

    return joinInfo.messageObservableMap.get(messageName)! as Observable<T>
  }

  private joinRoomAndHandleErrors(joinInfo: JoinInfo): void {
    this.joinRoom(joinInfo.joinRoomRequest)
      .then(roomIds => (joinInfo.roomIds = roomIds))
      .catch(() => {
        joinInfo.onErrorJoiningRoom?.()
      })
  }

  private async joinRoom(joinRoomRequest: JoinRoomRequest): Promise<string[]> {
    this.loggingService.debug({
      message: 'socket.io joining room',
      joinRoomRequest,
    })
    const joinResponse = (await this.socket.emitWithAck('join', classToPlain(joinRoomRequest))) as JoinRoomResponse
    if (joinResponse?.success === false) {
      this.loggingService.warn({
        message: 'Failed to successfully join room',
        joinResponse,
      })
      throw new Error('Failed to successfully join room')
    }
    if ('roomIds' in joinResponse) {
      return joinResponse.roomIds
    }
    if ('roomId' in joinResponse) {
      return [joinResponse.roomId]
    }
    assertUnreachable(joinResponse)
  }

  private leaveRooms(joinRef: string): void {
    const join = this.joins.get(joinRef)
    if (!join?.roomIds) {
      return
    }
    for (const id of join.roomIds) {
      this.loggingService.debug({
        message: 'socket.io leaving room',
        roomId: id,
      })
      this.socket.emit('leave', { roomId: id })
    }
  }

  private setupReconnect(): void {
    this.socket.io.on('reconnect', async () => {
      this.loggingService.debug({
        message: 'socket.io rejoining rooms',
      })

      // Note: Ideally we send this token as part of connect/reconnect of the ws socket connection itself so that we
      // have a consistent way of providing auth to the socket server. However, this was out of scope of trying to
      // patch authorisation for live-stream rooms (ST-2121)
      const auth = await this.authenticationService.currentJwtToken
      for (const joinInfo of this.joins.values()) {
        if ('auth' in joinInfo.joinRoomRequest) {
          joinInfo.joinRoomRequest = { ...joinInfo.joinRoomRequest, auth }
        }
        this.joinRoomAndHandleErrors(joinInfo)
      }
    })
  }

  private leaveAll(): void {
    this.loggingService.debug({
      message: 'socket.io leaving rooms',
    })
    this.socket.emit('leave')
  }

  private onAuthEvent(authEvent: AuthEvent): void {
    switch (authEvent.type) {
      case AuthEventType.Logout:
        this.leaveAll()
        break
      default:
        break
    }
  }

  private logEventsAndErrors(): void {
    this.socket.onAny((eventName: string, packet: any) => {
      this.loggingService.debug({
        message: 'socket.io received data',
        eventName,
        data: packet,
      })
    })

    this.socket.on('connect_error', (error: any) => {
      this.loggingService.warn({
        message: 'socket.io connection error',
        error,
      })
    })

    this.socket.on('connect_timeout', () => {
      this.loggingService.debug({
        message: 'socket.io connection timeout',
      })
    })
  }
}
